import { useAuth } from '@teko/auth-react';
import { Body, Caption, Col, Flex, Shape } from '@teko/ds-desktop';
import { DownOutlined, LocationPickerColored } from '@teko/ds-icon';
import { getTrackingData } from '@teko/tracking';
import { RegionPickerSuggestionPopover } from 'components/RegionPickerSuggestionPopover';
import {
  PopupRegionPicker,
  UserProfileHeaderV2,
} from 'components/desktop/organisms';
import {
  useFeatureFlags,
  useLocation,
  useThreeLevelLocationContext,
} from 'contexts';
import { getNavItems, isServerless } from 'helpers';
import { useLoyaltyMemberInfo, useRegionPicker, useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { CartOverview } from 'modules/cart';
import { NotificationOverview } from 'modules/notification';
import { SlotComponentProps } from 'modules/page-builder';
import { TerminalOverview } from 'modules/terminal';
import { useMemo } from 'react';
import {
  NavItem,
  defaultNavItemDisplayConfig,
} from 'tenants/_template/desktop/_module/components/NavigationBar';
import { HeaderRightItemDesktop as OldLayoutHeaderRightItemDesktop } from './HeaderRightItemDesktop';

type HeaderRightItemProps = SlotComponentProps<{}>;

const NewLayoutHeaderRightItemDesktop: React.FC<
  HeaderRightItemProps
> = ({}) => {
  const {
    partner,
    tenant,
    isDmsFlow,
    epicOmni1541,
    epicOmni1584Phase2,
    notificationAppId,
    epicOmni1538,
    notFetchLoyaltyMemberInfoGlobally,
    displayNewLayout,
  } = useTenantConfig();
  const { user } = useAuth();
  const { loyaltyMemberInfo } = useLoyaltyMemberInfo(
    !notFetchLoyaltyMemberInfoGlobally,
  );
  const { flags } = useFeatureFlags();
  const regionPickerProps = useRegionPicker();
  const region = regionPickerProps?.activeLocation;

  const {
    setShowed: showLocationPopup,
    selectedLocation,
    showRegionPickerSuggestionPopover,
    onCloseRegionPickerSuggestionPopover,
    enabled: locationPickerEnabled,
  } = useLocation();
  const { correctThreeLevelLocation, onOpenThreeLevelLocationPopup } =
    useThreeLevelLocationContext();

  const profileLinks = useMemo(() => {
    return getNavItems({
      tenant,
      logged: !!user?.userId,
      showLoyaltyItem:
        (notFetchLoyaltyMemberInfoGlobally
          ? !!user?.loyaltyMemberId
          : !!loyaltyMemberInfo) || !!epicOmni1538,
      showNotificationItem: !!notificationAppId,
      epicOmni1546: true,
      displayNewLayout: displayNewLayout,
    });
  }, [tenant, user, loyaltyMemberInfo]);

  const RegionPickerIcon = ({ locationName }: { locationName?: string }) => {
    const showRegionPickerModal = () => {
      if (flags.epic_ecom_284) {
        showLocationPopup(true);
      } else {
        regionPickerProps.onOpenPopup();
      }
    };

    // Don't show this icon on the server side
    if (!isServerless()) {
      return null;
    }

    return (
      <RegionPickerSuggestionPopover
        active={showRegionPickerSuggestionPopover}
        onClose={onCloseRegionPickerSuggestionPopover}
      >
        <NavItem
          onClick={showRegionPickerModal}
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'location',
          })}
          className="att-home-page-delivery-area"
        >
          <LocationPickerColored size={20} />
          <Shape>
            <Caption color="textSecondary">{t('Xem sản phẩm tại')}</Caption>
            <Body maxWidth="128px" line={1} level={2} weight="medium">
              {locationName}
            </Body>
          </Shape>
          <DownOutlined size={20} />
        </NavItem>
      </RegionPickerSuggestionPopover>
    );
  };

  const ThreeLevelLocationPickerIcon = () => {
    return (
      <RegionPickerSuggestionPopover
        active={!!showRegionPickerSuggestionPopover}
        onClose={onCloseRegionPickerSuggestionPopover ?? (() => {})}
      >
        <NavItem
          onClick={onOpenThreeLevelLocationPopup}
          className="att-home-page-delivery-area"
        >
          <LocationPickerColored size={20} />
          <Shape>
            <Caption color="textSecondary">{t('Xem sản phẩm tại')}</Caption>
            {!!correctThreeLevelLocation ? (
              <Body level={2} weight="medium" maxWidth="128px" line={1}>
                {correctThreeLevelLocation.ward.label},{' '}
                {correctThreeLevelLocation.district?.label},{' '}
                {correctThreeLevelLocation.province.label}
              </Body>
            ) : (
              <Body level={2} weight="medium">
                {t('Chưa có địa chỉ được chọn')}
              </Body>
            )}
          </Shape>
          <DownOutlined size={20} />
        </NavItem>
      </RegionPickerSuggestionPopover>
    );
  };

  const locationPicker = () => {
    if (locationPickerEnabled) {
      return <RegionPickerIcon locationName={selectedLocation?.locationName} />;
    } else if (
      regionPickerProps?.isShowLocationIcon &&
      region &&
      regionPickerProps?.terminals?.length > 1
    ) {
      return <RegionPickerIcon locationName={region?.label} />;
    }
    if (!isDmsFlow && !!epicOmni1541) {
      return <ThreeLevelLocationPickerIcon />;
    }
    if (!isDmsFlow && !!epicOmni1584Phase2) {
      return <TerminalOverview />;
    }
  };

  const navItems = useMemo(() => {
    const { login, cart, additional } = defaultNavItemDisplayConfig;
    const navMenu = [];
    if (login)
      navMenu.push(<UserProfileHeaderV2 profileLinks={profileLinks} />);
    if (notificationAppId) navMenu.push(<NotificationOverview />);
    if (cart) navMenu.push(<CartOverview showShippingFeeMiniCart={true} />);
    if (additional) navMenu.push(additional);
    return navMenu;
  }, [
    epicOmni1541,
    isDmsFlow,
    locationPickerEnabled,
    notificationAppId,
    partner,
    profileLinks,
    region,
    selectedLocation?.locationName,
    tenant,
    user?.userId,
    correctThreeLevelLocation,
  ]);

  return (
    <Flex flexWrap="nowrap" gap="s20" align="center" justify="end">
      <Flex
        flexWrap="nowrap"
        gap="s24"
        align="center"
        justify="end"
        borderRight="1px solid divider"
        paddingRight="s20"
      >
        {navItems.map((item, index) => (
          <Col key={index}>{item}</Col>
        ))}
      </Flex>
      {locationPicker()}
      {!flags?.epic_ecom_284 && <PopupRegionPicker {...regionPickerProps} />}
    </Flex>
  );
};

export const HeaderRightItemDesktop: React.FC<HeaderRightItemProps> = ({}) => {
  const { displayNewLayout } = useTenantConfig();
  return displayNewLayout ? (
    <NewLayoutHeaderRightItemDesktop />
  ) : (
    <OldLayoutHeaderRightItemDesktop />
  );
};
